import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MessageSquare, ChevronUp } from "lucide-react";
import { listIdeas, LAB_STATUSES } from "@/lib/lab.functions";
import { CategoryChip } from "@/components/lab/StatusBadge";

export const Route = createFileRoute("/lab/roadmap")({ component: RoadmapPage });

const COLUMNS = [
  { status: "analysis" as const, label: "Análise", tone: "amber" },
  { status: "in_progress" as const, label: "Em dev", tone: "sky" },
  { status: "launched" as const, label: "Lançado", tone: "emerald" },
  { status: "declined" as const, label: "Recusado", tone: "zinc" },
];

const DOT: Record<string, string> = {
  amber: "bg-amber-500",
  violet: "bg-violet-500",
  sky: "bg-sky-500",
  emerald: "bg-emerald-500",
  zinc: "bg-zinc-500",
};

function RoadmapPage() {
  const list = useServerFn(listIdeas);
  const q = useQuery({
    queryKey: ["lab-roadmap"],
    queryFn: () => list({ data: { filter: "top", limit: 200 } }),
  });
  const items = q.data ?? [];
  return (
    <div>
      <div className="mb-4 text-sm text-muted-foreground">
        O que está sendo construído, o que já foi lançado e o que a comunidade está pedindo.
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {COLUMNS.map((col) => {
          const cards = items.filter((i) => i.status === col.status);
          return (
            <div key={col.status} className="rounded-2xl border border-[color:var(--hairline)] bg-surface p-3">
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground">
                  <span className={`inline-block h-2 w-2 rounded-full ${DOT[col.tone]}`} />
                  {col.label}
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground">{cards.length}</span>
              </div>
              <div className="grid gap-2">
                {cards.length === 0 && (
                  <div className="rounded-xl border border-dashed border-[color:var(--hairline)] p-4 text-center text-[11px] text-muted-foreground">
                    Vazio
                  </div>
                )}
                {cards.map((i) => (
                  <Link
                    key={i.id}
                    to="/lab/$id"
                    params={{ id: i.id }}
                    className="block rounded-xl border border-[color:var(--hairline)] bg-background p-3 transition-colors hover:border-primary/40"
                  >
                    <div className="mb-1.5"><CategoryChip category={i.category} /></div>
                    <div className="line-clamp-3 text-[13px] font-semibold leading-snug text-foreground">
                      {i.title}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums">
                        <span className="inline-flex items-center gap-1"><ChevronUp className="h-3 w-3" strokeWidth={3} />{i.vote_count}</span>
                        <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{i.comment_count}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {i.author?.avatar_url ? (
                          <img loading="lazy" decoding="async" src={i.author.avatar_url} alt="" className="h-4 w-4 rounded-full object-cover" />
                        ) : (
                          <div className="h-4 w-4 rounded-full bg-background" />
                        )}
                        <span className="max-w-[80px] truncate text-[11px] text-muted-foreground">@{i.author?.username ?? "anônimo"}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 text-xs text-muted-foreground">
        As prioridades e o roadmap oficial são decididos exclusivamente pela equipe da Viny Club.
      </div>
      <div className="sr-only">{LAB_STATUSES.map(s => s.label).join(", ")}</div>
    </div>
  );
}
